/**
 * KBVersionAccordion — expandable version history per KB document.
 * FR-080: version chain shown with current version highlighted.
 * WCAG 2.1 AA: accordion button with aria-expanded, aria-controls.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useKBVersionHistory } from '@/hooks/useKBDocuments';

interface KBVersionAccordionProps {
  docId: string;
  currentVersion: number;
  className?: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function KBVersionAccordion({
  docId,
  currentVersion,
  className,
}: KBVersionAccordionProps) {
  const [open, setOpen] = useState(false);
  const panelId = `kb-versions-${docId}`;
  const triggerId = `kb-versions-trigger-${docId}`;

  const { data, isLoading, error } = useKBVersionHistory(open ? docId : null);

  return (
    <div className={cn('rounded-md border border-border', className)}>
      <button
        id={triggerId}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => { setOpen((v) => !v); }}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-foreground',
          'hover:bg-muted/40 transition-colors rounded-md',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <span>Version History</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        )}
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!open}
        className="border-t border-border px-4 pb-4 pt-3"
      >
        {isLoading && (
          <div className="space-y-2" aria-label="Loading version history">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        )}

        {error && (
          <p role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Failed to load version history.
          </p>
        )}

        {data && (
          <ol className="space-y-2" aria-label={`${data.total_versions} versions`}>
            {data.versions.map((v) => {
              const isCurrent = v.version === currentVersion;
              return (
                <li
                  key={v.id}
                  className={cn(
                    'flex items-start justify-between rounded-md px-3 py-2 text-sm',
                    isCurrent ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/30',
                  )}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">v{v.version}</span>
                      {isCurrent && (
                        <Badge className="text-xs bg-primary/20 text-primary border-primary/30">
                          Current
                        </Badge>
                      )}
                      {!isCurrent && (
                        <Badge variant="secondary" className="text-xs">
                          Superseded
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Uploaded {formatDate(v.created_at)}
                      {v.indexed_at ? ` · Indexed ${formatDate(v.indexed_at)}` : ' · Indexing…'}
                    </p>
                    {v.effective_date && (
                      <p className="text-xs text-muted-foreground">
                        Effective {formatDate(v.effective_date)}
                      </p>
                    )}
                  </div>
                  {!v.indexed_at && (
                    <Loader2
                      className="mt-0.5 h-4 w-4 animate-spin text-blue-500 shrink-0"
                      aria-label="Indexing in progress"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
